// Bepaalt het UTC-vrijgavemoment voor een loonstrook (geplande vrijgave +
// auto-planning). Puur; alle NL<->UTC-conversie via de DST-correcte helpers in
// amsterdam-tijd.ts (CLAUDE.md: geen datum-library). De standaard-dag is per
// schema 1-28, dus elke maand bestaat die dag (geen rollover-edge).

import type { LoonstrookAutoPlanResultaatDTO, LoonstrookVerstrekenMomentGedrag } from '@streep/types'
import { amsterdamWallclockToUtc, utcToAmsterdamWallclock } from './amsterdam-tijd'

const pad = (n: number) => String(n).padStart(2, '0')
const ymd = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`

/**
 * Eerstvolgende `dag@uur` (Europe/Amsterdam) vanaf `now`, nooit in het verleden.
 * Gebruikt voor de handmatige kiezer-voorinvulling en als FOUR_WEEK-fallback.
 * Retourneert het UTC-instant.
 */
export function eerstvolgendVrijgaveMoment(now: Date, dag: number, uur: number): Date {
  const { ymd: nowYmd } = utcToAmsterdamWallclock(now)
  const [y, m] = nowYmd.split('-').map(Number)
  const candidate = amsterdamWallclockToUtc(ymd(y, m, dag), `${pad(uur)}:00`)
  if (candidate.getTime() > now.getTime()) return candidate
  const nextM = m === 12 ? 1 : m + 1
  const nextY = m === 12 ? y + 1 : y
  return amsterdamWallclockToUtc(ymd(nextY, nextM, dag), `${pad(uur)}:00`)
}

/**
 * Standaard vrijgave-moment voor een loonstrook-periode (auto-planning).
 *
 * MONTH-loonstrook: `dag@uur` in de loonmaand zelf (bijv. juni-strook -> 24 juni
 * 08:00 NL). Ligt dat al in het verleden (late upload), dan blijft dat moment
 * staan; de cron geeft de strook bij de eerstvolgende tik (<=15 min) vrij.
 *
 * FOUR_WEEK (of ontbrekende maand): eerstvolgende `dag@uur` vanaf `now`.
 *
 * Retourneert het UTC-instant.
 */
export function standaardVrijgaveMomentVoorPeriode(
  p: { jaar: number; maand: number | null; periodeType: 'MONTH' | 'FOUR_WEEK' },
  dag: number,
  uur: number,
  now: Date,
): Date {
  if (p.periodeType === 'MONTH' && p.maand != null) {
    return amsterdamWallclockToUtc(ymd(p.jaar, p.maand, dag), `${pad(uur)}:00`)
  }
  return eerstvolgendVrijgaveMoment(now, dag, uur)
}

// ─── batch-66 (FO-492) — de automaat: plannen na goedkeuring ─────────────────
//
// Een strook die als goedgekeurd geldt krijgt automatisch het standaardmoment
// van zijn periode. Deze functies beslissen WAT de automaat doet; de API voert
// het uit. Puur en op UTC-instants: een vergelijking van twee instants is
// inherent DST-veilig, en de enige NL<->UTC-vertaling (dag@uur van de periode)
// loopt via `amsterdamWallclockToUtc`.

const UUR_MS = 3_600_000

/** Hoe ver vooruit de melding "nog niet goedgekeurd" kijkt: 24 uur. */
export const LOONSTROOK_VRIJGAVE_MELDVENSTER_MS = 24 * UUR_MS

/**
 * Het eerstvolgende hele uur STRIKT na `now` (UTC-instant). Europe/Amsterdam
 * loopt altijd een heel aantal uren voor op UTC, dus een heel uur in UTC is ook
 * een heel uur in NL-tijd, ook op de dagen dat de klok verspringt.
 */
export function eerstvolgendHeleUur(now: Date): Date {
  return new Date(Math.floor(now.getTime() / UUR_MS) * UUR_MS + UUR_MS)
}

export interface LoonstrookAutoPlanInput {
  now: Date
  periode: { jaar: number; maand: number | null; periodeType: 'MONTH' | 'FOUR_WEEK' }
  /** Bedrijfsbrede standaard: dag van de maand (1-28) en uur (0-23), NL-tijd. */
  standaardDag: number
  standaardUur: number
  verstrekenMomentGedrag: LoonstrookVerstrekenMomentGedrag
  /**
   * Het spoor van een menselijk besluit op de strook: wie plande (null = de
   * automaat, of nog nooit gepland) en het moment dat daarbij hoort.
   */
  geplandDoor: string | null
  geplandeVrijgaveOp: Date | null
  /**
   * Gezet wanneer een mens de strook na het klaarzetten introk en hij sindsdien
   * niet opnieuw is gepland of klaargezet (dat wist het veld). Zo'n strook kan
   * via een afkeuring weer in concept komen; de automaat geeft hem dan niet
   * opnieuw vrij.
   */
  ingetrokkenOp: Date | null
}

export type LoonstrookAutoPlanActie =
  /**
   * Een mens haalde de strook uit de planning (plan geannuleerd) of trok hem na
   * het klaarzetten in: de automaat blijft eraf.
   */
  | { actie: 'OVERSLAAN'; reden: 'HANDMATIG_UIT_PLANNING' | 'HANDMATIG_INGETROKKEN' }
  /**
   * Plannen op `vrijgaveOp`. `handmatigMoment` = het eerder door een mens
   * gekozen moment wordt opnieuw gebruikt (de strook erft het).
   */
  | { actie: 'PLANNEN'; vrijgaveOp: Date; handmatigMoment: boolean }
  /** Het moment is voorbij en de instelling zegt: direct vrijgeven. */
  | { actie: 'DIRECT_KLAARZETTEN' }

/**
 * Wat doet de automaat met een goedgekeurde strook die nog in CONCEPT staat?
 *
 *  1. Een mens annuleerde de planning (wél een planner, géén moment) of trok de
 *     strook na het klaarzetten in: overslaan. Een handmatig besluit wordt
 *     nooit door de automaat overschreven.
 *  2. Er staat nog een handmatig gekozen moment op de strook (hij kwam terug uit
 *     AFGEKEURD, bv. na een gecorrigeerde versie): dat moment blijft gelden.
 *  3. Anders het standaardmoment van de periode.
 *
 * Ligt het gekozen moment in de toekomst, dan plannen. Is het al voorbij (late
 * verwerking), dan beslist `verstrekenMomentGedrag`: direct klaarzetten, of
 * plannen op het eerstvolgende hele uur.
 */
export function bepaalLoonstrookAutoPlanActie(input: LoonstrookAutoPlanInput): LoonstrookAutoPlanActie {
  const { now } = input
  if (input.ingetrokkenOp != null) {
    return { actie: 'OVERSLAAN', reden: 'HANDMATIG_INGETROKKEN' }
  }
  if (input.geplandDoor != null && input.geplandeVrijgaveOp == null) {
    return { actie: 'OVERSLAAN', reden: 'HANDMATIG_UIT_PLANNING' }
  }

  const handmatigMoment = input.geplandDoor != null && input.geplandeVrijgaveOp != null
  const moment =
    handmatigMoment && input.geplandeVrijgaveOp
      ? input.geplandeVrijgaveOp
      : standaardVrijgaveMomentVoorPeriode(
          input.periode,
          input.standaardDag,
          input.standaardUur,
          now,
        )

  if (moment.getTime() > now.getTime()) {
    return { actie: 'PLANNEN', vrijgaveOp: moment, handmatigMoment }
  }
  if (input.verstrekenMomentGedrag === 'EERSTVOLGEND_HEEL_UUR') {
    // Het oorspronkelijke moment is voorbij; dit nieuwe moment koos de automaat.
    return { actie: 'PLANNEN', vrijgaveOp: eerstvolgendHeleUur(now), handmatigMoment: false }
  }
  return { actie: 'DIRECT_KLAARZETTEN' }
}

export interface NaderendeVrijgavePeriode {
  jaar: number
  maand: number
  /** Het standaard vrijgavemoment van die maandperiode (UTC-instant). */
  vrijgaveOp: Date
}

/**
 * De maandperiodes waarvan het standaard vrijgavemoment binnen het venster
 * (default 24 uur) na `now` valt: `moment - venster <= now < moment`.
 *
 * Voedt de melding "nog n stroken niet goedgekeurd". Het venster is in echte
 * uren gemeten, niet in wandklok: rond een klokwissel is "24 uur van tevoren"
 * dus een uur vroeger of later op de klok, maar altijd precies 24 uur.
 *
 * De lopende NL-maand en de volgende volstaan: de standaarddag is 1-28, dus het
 * moment van de volgende maand kan binnen 24 uur liggen (dag 1), dat van de
 * maand daarna nooit.
 */
export function periodesMetNaderendeVrijgave(
  now: Date,
  dag: number,
  uur: number,
  vensterMs: number = LOONSTROOK_VRIJGAVE_MELDVENSTER_MS,
): NaderendeVrijgavePeriode[] {
  const { ymd: nowYmd } = utcToAmsterdamWallclock(now)
  const [jaar, maand] = nowYmd.split('-').map(Number) as [number, number]
  const kandidaten = [
    { jaar, maand },
    maand === 12 ? { jaar: jaar + 1, maand: 1 } : { jaar, maand: maand + 1 },
  ]
  const uit: NaderendeVrijgavePeriode[] = []
  for (const k of kandidaten) {
    const vrijgaveOp = standaardVrijgaveMomentVoorPeriode(
      { jaar: k.jaar, maand: k.maand, periodeType: 'MONTH' },
      dag,
      uur,
      now,
    )
    const t = vrijgaveOp.getTime()
    if (t - vensterMs <= now.getTime() && now.getTime() < t) {
      uit.push({ jaar: k.jaar, maand: k.maand, vrijgaveOp })
    }
  }
  return uit
}

/**
 * De maandperiodes die het vangnet in de kwartiertik bewaakt: de periodes
 * waarvan het standaard vrijgavemoment nog moet komen (de lopende NL-maand
 * zolang het moment niet voorbij is, en de volgende maand), plus de LAATST
 * verstreken periode.
 *
 * Een strook van zo'n periode die goedgekeurd is maar nog niet gepland staat
 * wordt alsnog opgepakt: gepland op het standaardmoment, of (het moment is
 * voorbij) behandeld als late verwerking volgens de instelling. De laatst
 * verstreken periode hoort erbij omdat afwijkingen juist kort ná het moment nog
 * worden opgelost; oudere periodes niet, zodat het vangnet nooit ongevraagd
 * oude stroken vrijgeeft.
 *
 * De standaarddag is 1-28: het moment van de vorige maand is altijd voorbij en
 * dat van de volgende maand ligt altijd in de toekomst. Oplopend op moment.
 */
export function periodesVoorVangnet(
  now: Date,
  dag: number,
  uur: number,
): NaderendeVrijgavePeriode[] {
  const { ymd: nowYmd } = utcToAmsterdamWallclock(now)
  const [jaar, maand] = nowYmd.split('-').map(Number) as [number, number]
  const vorige = maand === 1 ? { jaar: jaar - 1, maand: 12 } : { jaar, maand: maand - 1 }
  const volgende = maand === 12 ? { jaar: jaar + 1, maand: 1 } : { jaar, maand: maand + 1 }
  const metMoment = (k: { jaar: number; maand: number }): NaderendeVrijgavePeriode => ({
    jaar: k.jaar,
    maand: k.maand,
    vrijgaveOp: standaardVrijgaveMomentVoorPeriode(
      { jaar: k.jaar, maand: k.maand, periodeType: 'MONTH' },
      dag,
      uur,
      now,
    ),
  })
  const lopend = metMoment({ jaar, maand })
  const lopendVoorbij = lopend.vrijgaveOp.getTime() <= now.getTime()
  // Het moment van de lopende maand is voorbij: dat IS de laatst verstreken
  // periode. Anders is dat de vorige maand en komt de lopende nog.
  return lopendVoorbij
    ? [lopend, metMoment(volgende)]
    : [metMoment(vorige), lopend, metMoment(volgende)]
}

/**
 * Wat de automaat deed, in gewone taal, voor de melding in het scherm na een
 * actie die de goedkeuring van stroken kan veranderen ("Controle herdraaien",
 * een voertuig koppelen). `null` als er niets gebeurde.
 *
 * De vrijgave staat vooraan: dat een strook nu bij de medewerker ligt is het
 * eerste wat de gebruiker moet weten, want dat is niet terug te draaien zonder
 * dat de medewerker het al gezien kan hebben.
 */
export function loonstrookAutoPlanSamenvatting(
  resultaat: LoonstrookAutoPlanResultaatDTO | null | undefined,
): string | null {
  if (!resultaat) return null
  const stroken = (n: number) => (n === 1 ? '1 loonstrook' : `${n} loonstroken`)
  const isZijn = (n: number) => (n === 1 ? 'is' : 'zijn')
  const zinnen: string[] = []
  if (resultaat.klaargezet > 0) {
    const n = resultaat.klaargezet
    zinnen.push(
      `${stroken(n)} ${isZijn(n)} hierdoor direct beschikbaar gezet voor de medewerker${n === 1 ? '' : 's'}.`,
    )
  }
  if (resultaat.gepland > 0) {
    const n = resultaat.gepland
    zinnen.push(`${stroken(n)} ${isZijn(n)} hierdoor ingepland voor vrijgave.`)
  }
  if (resultaat.teruggezet > 0) {
    const n = resultaat.teruggezet
    zinnen.push(`De automatische planning van ${stroken(n)} is vervallen.`)
  }
  return zinnen.length > 0 ? zinnen.join(' ') : null
}
